import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, LogOut, Cloud, Mail, ShieldCheck, ArrowRight, Settings as SettingsIcon, Save, Loader2, Building2, MapPin, UserRound, Camera, Phone, Hash } from "lucide-react";
import { api, mediaUrl, uploadAvatar } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useProStatus } from "@/hooks/useProStatus";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();
  const { data: pro } = useProStatus();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", company: "", home_address: "", phone: "", ein: "" });
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);

  useEffect(() => {
    document.title = "Profile · RigBooks";
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        company: user.company || "",
        home_address: user.home_address || "",
        phone: user.phone || "",
        ein: user.ein || "",
      });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api("/users/me", { method: "PATCH", body: form });
      await refresh();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err?.message || "Couldn't save your profile.");
    } finally {
      setSaving(false);
    }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5 MB).");
      return;
    }
    setAvatarBusy(true);
    try {
      await uploadAvatar(file);
      await refresh();
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(err?.message || "Couldn't upload photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const initials = (user?.name || user?.email || "D").slice(0, 1).toUpperCase();
  const providers = user?.linked_providers?.length ? user.linked_providers : [user?.auth_provider || "email"];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Identity */}
      <div className="flex items-center gap-4 rounded-2xl border border-rig-border bg-rig-surface p-6">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="profile-avatar-btn"
          className="group relative h-16 w-16 shrink-0 rounded-full"
          title="Change photo"
        >
          <Avatar className="h-16 w-16 border border-rig-borderStrong">
            {user?.picture && <AvatarImage src={mediaUrl(user.picture)} alt={user?.name} />}
            <AvatarFallback className="bg-rig-surface2 font-display text-2xl font-bold text-rig-accent">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className={`absolute inset-0 flex items-center justify-center rounded-full bg-black/50 transition ${avatarBusy ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
            {avatarBusy ? <Loader2 className="h-5 w-5 animate-spin text-white" /> : <Camera className="h-5 w-5 text-white" />}
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={onPickAvatar}
            data-testid="profile-avatar-input"
            className="hidden"
          />
        </button>
        <div className="min-w-0">
          <div className="truncate font-display text-2xl font-bold text-white">{user?.name || "Driver"}</div>
          <div className="flex items-center gap-1.5 text-sm text-rig-dim">
            <Mail className="h-3.5 w-3.5" /> <span className="truncate">{user?.email}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {providers.map((p) => (
              <span key={p} className="rounded-full bg-rig-surface2 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-rig-dim">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Editable profile details */}
      <form onSubmit={saveProfile} className="rounded-2xl border border-rig-border bg-rig-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-dim">
            Profile details
          </h2>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/settings")}
            data-testid="profile-open-settings"
            className="h-8 gap-1.5 px-2 text-xs text-rig-dim hover:text-white"
          >
            <SettingsIcon className="h-4 w-4" /> Settings
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name" className="flex items-center gap-1.5 font-display text-xs uppercase tracking-widest text-rig-dim">
              <UserRound className="h-3.5 w-3.5" /> Full name
            </Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              data-testid="profile-name-input"
              className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
            />
          </div>
          <div>
            <Label htmlFor="company" className="flex items-center gap-1.5 font-display text-xs uppercase tracking-widest text-rig-dim">
              <Building2 className="h-3.5 w-3.5" /> Company / business name
            </Label>
            <Input
              id="company"
              value={form.company}
              onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
              placeholder="e.g. Cascadia Freight LLC"
              data-testid="profile-company-input"
              className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
            />
          </div>
          <div>
            <Label htmlFor="home_address" className="flex items-center gap-1.5 font-display text-xs uppercase tracking-widest text-rig-dim">
              <MapPin className="h-3.5 w-3.5" /> Home address
            </Label>
            <Input
              id="home_address"
              value={form.home_address}
              onChange={(e) => setForm((f) => ({ ...f, home_address: e.target.value }))}
              placeholder="Street, city, state ZIP"
              data-testid="profile-address-input"
              className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
            />
            <p className="mt-1.5 text-xs text-rig-mute">Used for your tax-home state and on exported CPA reports.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="phone" className="flex items-center gap-1.5 font-display text-xs uppercase tracking-widest text-rig-dim">
                <Phone className="h-3.5 w-3.5" /> Phone
              </Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="(555) 123-4567"
                data-testid="profile-phone-input"
                className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
              />
            </div>
            <div>
              <Label htmlFor="ein" className="flex items-center gap-1.5 font-display text-xs uppercase tracking-widest text-rig-dim">
                <Hash className="h-3.5 w-3.5" /> Business EIN
              </Label>
              <Input
                id="ein"
                value={form.ein}
                onChange={(e) => setForm((f) => ({ ...f, ein: e.target.value }))}
                placeholder="12-3456789"
                data-testid="profile-ein-input"
                className="mt-1.5 h-12 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          data-testid="profile-save-btn"
          className="mt-6 h-12 w-full gap-2 bg-rig-accent font-display text-sm font-bold uppercase tracking-[0.2em] text-rig-accentText hover:bg-rig-accentDark"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          Save changes
        </Button>
      </form>

      {/* Pro status */}
      {pro?.is_pro ? (
        <div className="rounded-2xl border border-rig-accent/60 bg-rig-accent/10 p-6">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-rig-accent" />
            <span className="font-display text-lg font-bold uppercase tracking-widest text-rig-accent">RigBooks Pro</span>
          </div>
          <p className="mt-2 text-sm text-rig-dim">
            Unlimited receipts, IFTA, bookkeeper sharing &amp; the year-end audit bundle are unlocked.
          </p>
          {pro.pro_expires_at && (
            <p className="mt-1 text-xs text-rig-mute">Renews / expires {String(pro.pro_expires_at).slice(0, 10)}</p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-rig-border bg-rig-surface p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display text-lg font-bold uppercase tracking-widest text-white">Free Plan</div>
              {pro && (
                <p className="mt-1 text-sm text-rig-dim">
                  {pro.remaining} of {pro.free_limit} receipts left this month
                </p>
              )}
            </div>
            <Button
              data-testid="profile-upgrade-btn"
              onClick={() => navigate("/upgrade")}
              className="bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
            >
              Upgrade <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Cloud sync */}
      <div className="flex items-center gap-3 rounded-2xl border border-rig-border bg-rig-surface p-5">
        <Cloud className="h-5 w-5 text-rig-success" />
        <div className="flex-1">
          <div className="font-semibold text-white">Cloud sync active</div>
          <div className="text-sm text-rig-dim">Every receipt is backed up and timestamped for audits.</div>
        </div>
        <ShieldCheck className="h-6 w-6 text-rig-accent" />
      </div>

      <Button
        variant="outline"
        onClick={handleLogout}
        data-testid="profile-signout-btn"
        className="h-12 w-full border-rig-error/50 bg-transparent font-display text-sm font-bold uppercase tracking-widest text-rig-error hover:bg-rig-error/10"
      >
        <LogOut className="mr-2 h-4 w-4" /> Sign out
      </Button>
    </div>
  );
}
